import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface AdminFormDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /**
   * Pinned action bar. When present the body scrolls independently and the
   * footer stays visible — needed by drawers whose primary action must stay
   * reachable while a long list scrolls (e.g. bulk assign).
   */
  footer?: ReactNode;
  /** Tailwind width classes for the panel. Defaults to the 480px form width. */
  widthClassName?: string;
}

/**
 * AdminFormDrawer - Slide-out drawer for create/edit forms
 * Uses framer-motion for animations to match existing codebase patterns
 * Renders via portal to ensure proper fixed positioning at viewport level
 */
export function AdminFormDrawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  widthClassName = 'w-full sm:w-[480px]',
}: AdminFormDrawerProps) {
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-50"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={cn(
              'fixed right-0 top-0 bottom-0 bg-card border-l border-border z-50',
              widthClassName,
              footer ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'
            )}
          >
            {/* Header */}
            <div
              className={cn(
                'bg-card border-b border-border px-6 py-4 flex items-start justify-between',
                footer ? 'flex-none' : 'sticky top-0'
              )}
            >
              <div className="flex-1 pr-4">
                <h2 className="text-xl font-bold text-foreground">{title}</h2>
                {description && (
                  <p className="text-sm text-muted-foreground mt-1">{description}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Content */}
            <div className={cn('p-6', footer && 'flex-1 overflow-y-auto min-h-0')}>
              {children}
            </div>

            {footer && (
              <div className="flex-none border-t border-border bg-card px-6 py-4">
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
